import { MAX_FILE_SIZE, MAX_IMAGES } from './constants';
// Import the new functions from imageProcessing
import { 
  checkMemoryAvailability, 
  ProcessingProgress,
} from './imageProcessing';

export interface ImageValidationResult {
  valid: boolean;
  error?: string;
}

export function validateImageFile(file: File): ImageValidationResult {
  // Accept HEIC files even if MIME type is not set correctly
  if (!file.type.startsWith('image/') && !isHeicFile(file)) {
    return {
      valid: false,
      error: '画像ファイルのみアップロード可能です'
    };
  }

  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: 'ファイルサイズは10MB以下にしてください'
    };
  }

  return { valid: true };
}

// Helper function to convert blob to base64
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    console.log('📖 FileReader作成...');
    const reader = new FileReader();
    
    reader.onload = () => {
      console.log('✅ FileReader読み込み完了');
      const result = reader.result as string;
      console.log(`📏 Base64データサイズ: ${result.length}文字`);
      resolve(result);
    };
    
    reader.onerror = (error) => {
      console.error('❌ FileReader読み込み失敗:', error);
      console.error('🔍 FileReaderエラー詳細:', {
        error: reader.error,
        readyState: reader.readyState
      });
      reject(new Error(`FileReader failed: ${reader.error?.message || 'Unknown error'}`));
    };
    
    console.log('🚀 FileReader.readAsDataURL開始...');
    reader.readAsDataURL(blob);
  });
}

// Optimized thumbnail creation using createImageBitmap with quality control
export async function createThumbnail(file: File, quality: number = 0.8): Promise<string> {
  try {
    console.log(`🖼️ サムネイル作成開始: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
    console.log(`⚙️ 指定画質: ${Math.round(quality * 100)}%`);
    console.log('📋 ファイル情報:', {
      name: file.name,
      type: file.type,
      size: file.size,
      lastModified: file.lastModified
    });
    
    // Use createImageBitmap for more efficient memory usage
    console.log('🚀 createImageBitmap開始...');
    const imageBitmap = await createImageBitmap(file);
    console.log(`📐 画像サイズ取得: ${imageBitmap.width}x${imageBitmap.height}px`);
    console.log('✅ ImageBitmap作成成功');
    
    // Calculate thumbnail dimensions
    const maxWidth = 300;
    const maxHeight = 300;
    let width = imageBitmap.width;
    let height = imageBitmap.height;

    if (width > height) {
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }
    } else {
      if (height > maxHeight) {
        width = Math.round((width * maxHeight) / height);
        height = maxHeight;
      }
    }

    console.log(`📏 サムネイルサイズ決定: ${width}x${height}px`);
    
    // Create thumbnail
    console.log('🎨 Canvas要素作成開始...');
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    console.log(`📏 Canvas設定完了: ${canvas.width}x${canvas.height}px`);
    
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      console.error('❌ Canvas取得失敗: Canvas contextを取得できません');
      imageBitmap.close(); // Clean up ImageBitmap
      throw new Error('Failed to get canvas context');
    }

    // Use better quality settings
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    console.log('🎨 描画設定: 高品質アンチエイリアシングを有効化');
    
    // Draw and compress
    ctx.drawImage(imageBitmap, 0, 0, width, height);
    console.log('✏️ Canvas描画: 画像をCanvasに描画完了');
    
    // Clean up ImageBitmap immediately
    imageBitmap.close();
    console.log('🧹 メモリ解放: ImageBitmapをクリーンアップ');
    
    // Convert to blob first, then to base64 for memory efficiency
    return new Promise((resolve, reject) => {
      console.log(`🔄 変換処理: CanvasをJPEGに変換中... (品質: ${Math.round(quality * 100)}%)`);
      console.log(`⚙️ toBlob設定: JPEG品質${quality}`);
      
      try {
      canvas.toBlob((blob) => {
          console.log('📞 toBlobコールバック呼び出し');
        if (!blob) {
            console.error('❌ Blob変換失敗: サムネイルBlobの作成に失敗');
            console.error('🔍 Canvas状態:', {
              width: canvas.width,
              height: canvas.height,
              hasContext: !!ctx
            });
          reject(new Error('Failed to create thumbnail blob'));
          return;
        }
          console.log(`💾 Blob作成完了: ${(blob.size / 1024).toFixed(2)}KB`);
          console.log('📝 Blob詳細:', {
            size: blob.size,
            type: blob.type
          });
          
          console.log('🔄 Base64変換開始...');
          blobToBase64(blob)
            .then((base64) => {
              console.log(`✅ Base64変換完了: ${base64.length}文字`);
              resolve(base64);
            })
            .catch((error) => {
              console.error('❌ Base64変換失敗:', error);
              reject(error);
            });
      }, 'image/jpeg', quality);
      } catch (toBlobError) {
        console.error('❌ toBlob呼び出し失敗:', toBlobError);
        reject(new Error(`toBlob failed: ${toBlobError instanceof Error ? toBlobError.message : 'Unknown error'}`));
      }
    });
  } catch (error) {
    console.error('❌ サムネイル作成失敗:', error);
    console.error('🔍 エラー詳細:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined
    });
    throw new Error(`Failed to create thumbnail: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// Optimized image preview creation
export async function createImagePreview(file: File, thumbnailQuality: number = 0.8): Promise<{ original: string; thumbnail: string }> {
  if (file.size > 10 * 1024 * 1024) { // 10MB limit
    console.error(`❌ ファイルサイズ超過: ${file.name} は10MBを超えています`);
    throw new Error('File size exceeds 10MB limit');
  }

  try {
    console.log(`🔄 画像プレビュー作成開始: ${file.name}`);
    
    // Process thumbnail and original in parallel for better performance
    const [original, thumbnail] = await Promise.all([
      blobToBase64(file),
      createThumbnail(file, thumbnailQuality)
    ]);
    
    console.log(`✅ 画像プレビュー作成完了: ${file.name}`);
    return { original, thumbnail };
  } catch (error) {
    console.error(`❌ 画像プレビュー作成失敗: ${file.name}`, error);
    throw new Error(`Failed to create image preview: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

export interface ProcessImageBatchOptions {
  sortByFilename?: boolean;
}

// Process images in smaller batches to prevent memory issues
export async function processImageBatch(
  files: File[],
  onProgress?: (progress: ProcessingProgress) => void,
  thumbnailQuality: number = 0.8,
  options: ProcessImageBatchOptions = {}
): Promise<Array<{ original: string; thumbnail: string } | null>> {
  const boundedFiles = files.slice(0, 5000);
  const filesToProcess = options.sortByFilename
    ? boundedFiles
        .filter(file => file.type.startsWith('image/'))
        .sort((a, b) => a.name.localeCompare(b.name))
    : boundedFiles;
  
  console.log('🎯 processImageBatch開始:', {
    '入力ファイル数': files.length,
    '処理対象数': filesToProcess.length,
    'サムネイル画質': `${Math.round(thumbnailQuality * 100)}%`,
    'sortByFilename': options.sortByFilename ?? false
  });
  
  const results: Array<{ original: string; thumbnail: string } | null> =
    new Array(filesToProcess.length).fill(null);
  const batchSize = 5; // Process 5 images at a time to prevent memory spikes
  
  console.log(`⚡ バッチサイズ: ${batchSize}個ずつ並列処理を実行`);
  
  for (let i = 0; i < filesToProcess.length; i += batchSize) {
    const batch = filesToProcess.slice(i, i + batchSize);
    console.log(`📦 バッチ ${Math.floor(i/batchSize) + 1}/${Math.ceil(filesToProcess.length/batchSize)}: ${batch.length}個のファイルを処理中`);
    
    // Process batch in parallel
    const batchPromises = batch.map(async (file, batchIndex) => {
      const globalIndex = i + batchIndex;
      
      if (onProgress) {
        onProgress({
          current: globalIndex,
          total: filesToProcess.length,
          phase: 'processing'
        });
      }

      if (!file.type.startsWith('image/')) {
        console.warn(`⚠️ 画像${globalIndex + 1}スキップ: ${file.name} (非画像MIME)`);
        return { result: null, index: globalIndex };
      }

      try {
        console.log(`🎨 画像${globalIndex + 1}/${filesToProcess.length}: ${file.name} の処理開始`);
        const result = await createImagePreview(file, thumbnailQuality);
        console.log(`✅ 画像${globalIndex + 1}完了: ${file.name}`);
        return { result, index: globalIndex };
      } catch (error) {
        console.error(`❌ 画像${globalIndex + 1}失敗: ${file.name}`, error);
        return { result: null, index: globalIndex };
      }
    });

    // Wait for all files in the batch to complete
    const batchResults = await Promise.all(batchPromises);
    
    for (const batchResult of batchResults) {
      results[batchResult.index] = batchResult.result;
    }
    
    // Small delay between batches to allow garbage collection
    if (i + batchSize < filesToProcess.length) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
  
  console.log(`🎉 processImageBatch完了: ${results.filter(Boolean).length}個の画像を処理`);
  return results;
}

// Add a function to process a single image file efficiently
export async function processSingleImage(file: File, thumbnailQuality: number = 0.8): Promise<{ original: string; thumbnail: string }> {
  try {
    return await createImagePreview(file, thumbnailQuality);
  } catch (error) {
    console.error(`Failed to process single image: ${file.name}`, error);
    throw error;
  }
}

export async function convertHeicToJpeg(file: File): Promise<File> {
  try {
    console.log(`🔄 HEIC変換開始: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
    
    const heic2any = await import('heic2any');
    
    const convertedBlob = await heic2any.default({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.9
    });
    
    const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
    
    const originalName = file.name;
    const nameWithoutExt = originalName.replace(/\.(heic|heif)$/i, '');
    const newFileName = `${nameWithoutExt}.jpg`;
    
    console.log(`📎 ファイル名変更: ${originalName} → ${newFileName}`);
    console.log(`📦 変換後サイズ: ${(blob.size / 1024 / 1024).toFixed(2)}MB`);
    
    const convertedFile = new File([blob], newFileName, {
      type: 'image/jpeg',
      lastModified: file.lastModified
    });
    
    console.log(`✅ HEIC変換完了: ${newFileName}`);
    return convertedFile;
  } catch (error) {
    console.error('❌ HEIC変換失敗:', error);
    throw new Error(`HEIC変換に失敗しました: ${file.name}`);
  }
}

export function isHeicFile(file: File): boolean {
  // Check by MIME type first
  if (file.type === 'image/heic' || file.type === 'image/heif') {
    return true;
  }
  
  // Check by file extension as fallback (some browsers don't set MIME type correctly)
  const fileName = file.name.toLowerCase();
  return fileName.endsWith('.heic') || fileName.endsWith('.heif');
}

export function validateImageCount(currentCount: number, addCount: number): ImageValidationResult {
  const totalCount = currentCount + addCount;
  
  if (totalCount > MAX_IMAGES) {
    return {
      valid: false,
      error: `画像は最大${MAX_IMAGES}枚までアップロードできます (あと${MAX_IMAGES - currentCount}枚追加可能)`
    };
  }

  return { valid: true };
}

export async function processImageFiles(
  fileList: FileList | null,
  currentImages: string[],
  currentThumbnailImages: string[],
  additionalFiles?: File[],
  preserveOrder: boolean = true
): Promise<{ originals: string[]; thumbnails: string[] }> {
  console.log('🚀 processImageFiles: 画像処理を開始します');
  
  // Check memory availability before processing
  if (!checkMemoryAvailability()) {
    console.warn('Low memory detected, limiting image processing');
  }
  
  // Calculate remaining slots
  const remainingSlots = MAX_IMAGES - currentImages.length;
  console.log(`📝 スロット確認: 現在${currentImages.length}枚、残り${remainingSlots}スロット利用可能`);
  
  if (remainingSlots <= 0) {
    console.log('⚠️ 制限到達: 画像の上限に達しているため処理をスキップします');
    return { originals: currentImages, thumbnails: currentThumbnailImages };
  }

  // Convert FileList to array and combine with additional files
  const files = [
    ...(fileList ? Array.from(fileList) : []),
    ...(additionalFiles || [])
  ];

  // Limit the number of files to prevent memory issues on mobile
  const maxFiles = Math.min(files.length, remainingSlots, 20); // Max 20 files at once
  const filesToProcess = files.slice(0, maxFiles);
  console.log(`📋 ファイル処理: ${filesToProcess.length}個のファイルを処理対象として選択`);

  // Process files and convert HEIC files to JPEG
  // Process files in parallel batches for better performance
  const batchSize = 4; // Process 4 files at a time to balance speed and memory usage
  const processedFiles: File[] = [];
  const errors: string[] = [];

  console.log(`🔄 バッチ処理: ${batchSize}ファイルずつ処理を開始`);
  for (let i = 0; i < filesToProcess.length; i += batchSize) {
    const batch = filesToProcess.slice(i, i + batchSize);
    console.log(`📦 バッチ ${Math.floor(i/batchSize) + 1}: ${batch.length}個のファイルを処理中...`);
    
    // Process batch in parallel
    const batchPromises = batch.map(async (file) => {
      try {
        if (isHeicFile(file)) {
          console.log(`🔄 HEIC変換: ${file.name} を JPEG に変換中...`);
          const convertedFile = await convertHeicToJpeg(file);
          console.log(`✅ HEIC変換: ${file.name} の変換が完了`);
          
          // Validate the converted file
          const validation = validateImageFile(convertedFile);
          if (validation.valid) {
            return { success: true, file: convertedFile, originalName: file.name };
          } else {
            console.log(`❌ 検証失敗: ${file.name} - ${validation.error}`);
            return { success: false, error: `${file.name}: ${validation.error}` };
          }
        } else {
          // Validate non-HEIC files
          const validation = validateImageFile(file);
          if (validation.valid) {
            console.log(`✅ 検証成功: ${file.name}`);
            return { success: true, file, originalName: file.name };
          } else {
            console.log(`❌ 検証失敗: ${file.name} - ${validation.error}`);
            return { success: false, error: `${file.name}: ${validation.error}` };
          }
        }
      } catch (error) {
        console.error(`❌ ファイル処理エラー: ${file.name}`, error);
        return { 
          success: false, 
          error: `${file.name}: ${error instanceof Error ? error.message : '処理に失敗しました'}` 
        };
      }
    });
    
    // Wait for all files in the batch to complete
    const batchResults = await Promise.all(batchPromises);
    console.log(`✅ バッチ完了: ${batch.length}個のファイル処理が完了`);
    
    // Collect results
    for (const result of batchResults) {
      if (result.success) {
        if(result.file) processedFiles.push(result.file);
      } else {
        if(result.error) errors.push(result.error);
      }
    }
    
    // Small delay between batches to allow garbage collection
    if (i + batchSize < filesToProcess.length) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  }

  // Show errors if any
  if (errors.length > 0) {
    console.warn('⚠️ 一部ファイルの処理に失敗:', errors);
  }

  // Sort by filename only if not preserving order (drag & drop case)
  const finalFiles = preserveOrder 
    ? processedFiles.slice(0, remainingSlots)
    : processedFiles
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, remainingSlots);

  console.log(`📊 最終処理対象: ${finalFiles.length}個のファイル`);
  if (finalFiles.length === 0) {
    console.log('⚠️ 処理対象なし: 現在の画像データをそのまま返します');
    return { originals: currentImages, thumbnails: currentThumbnailImages };
  }

  // Use the optimized batch processing
  try {
    console.log('🎨 サムネイル生成: processImageBatch を開始...');
    const batchResults = await processImageBatch(finalFiles, undefined, 0.8, {
      sortByFilename: !preserveOrder
    });
    const processedImages = batchResults.filter(
      (img): img is { original: string; thumbnail: string } => img !== null
    );
    console.log(`✅ サムネイル生成完了: ${processedImages.length}個の画像セットを生成`);
    
    const newOriginals = processedImages.map(img => img.original);
    const newThumbnails = processedImages.map(img => img.thumbnail);
    
    // Return combined array of current and new images
    const totalOriginals = [...currentImages, ...newOriginals].slice(0, MAX_IMAGES);
    const totalThumbnails = [...currentThumbnailImages, ...newThumbnails].slice(0, MAX_IMAGES);
    
    console.log('🎉 processImageFiles完了:', {
      '最終オリジナル画像数': totalOriginals.length,
      '最終サムネイル画像数': totalThumbnails.length,
      '新規追加': processedImages.length
    });
    
    // 🔍 デバッグ用：最終的に返されるサムネイルデータの詳細確認
    console.log('🔍 最終返却データ詳細確認:', {
      'currentThumbnailImages配列の内容': currentThumbnailImages,
      'newThumbnails配列の内容': newThumbnails,
      'totalThumbnails配列の内容': totalThumbnails,
      'processedImages詳細': processedImages.map((img, index) => ({
        index,
        'originalExists': !!img.original,
        'thumbnailExists': !!img.thumbnail,
        'thumbnailSize': img.thumbnail ? img.thumbnail.length : 0
      }))
    });
    
    return { originals: totalOriginals, thumbnails: totalThumbnails };
  } catch (error) {
    console.error('❌ バッチ処理失敗:', error);
    
    // Fallback to sequential processing for smaller batches
    console.log('🔄 フォールバック: 順次処理に切り替えます');
    return await processFilesSequentially(finalFiles, currentImages, currentThumbnailImages);
  }
}

// Fallback sequential processing for when batch processing fails
async function processFilesSequentially(files: File[], currentImages: string[], currentThumbnailImages: string[]): Promise<{ originals: string[]; thumbnails: string[] }> {
  const newOriginals: string[] = [];
  const newThumbnails: string[] = [];
  
  for (const file of files) {
    try {
      const { original, thumbnail } = await createImagePreview(file);
      newOriginals.push(original);
      newThumbnails.push(thumbnail);
    } catch (error) {
      console.error(`Failed to process file ${file.name}:`, error);
    }
  }

  // Return combined array of current and new images
  const totalOriginals = [...currentImages, ...newOriginals].slice(0, MAX_IMAGES);
  const totalThumbnails = [...currentThumbnailImages, ...newThumbnails].slice(0, MAX_IMAGES);
  return { originals: totalOriginals, thumbnails: totalThumbnails };
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}


export function getOriginalFilename(dataUrl: string): string {
  try {
    // Check if the data URL contains filename metadata
    const metadataMatch = dataUrl.match(/;filename=([^;]+)/);
    if (metadataMatch) {
      return decodeURIComponent(metadataMatch[1]);
    }

    // For base64 images without metadata, get the correct extension
    if (dataUrl.startsWith('data:image/')) {
      const mimeType = dataUrl.split(';')[0].split(':')[1];
      const extension = mimeType.split('/')[1].replace('jpeg', 'jpg');
      return `${Date.now()}.${extension}`;
    }

    // For URLs, extract the filename and extension
    const urlParts = dataUrl.split('/');
    const filename = urlParts[urlParts.length - 1];
    if (filename.includes('.')) {
      const extension = filename.split('.').pop()?.toLowerCase();
      if (extension && ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
        return filename;
      }
    }

    // Default to jpg if no extension could be determined
    return `${Date.now()}.jpg`;
  } catch (error) {
    return `${Date.now()}.jpg`;
  }
}

export function getSequentialFilename(index: number): string {
  return `${String(index).padStart(6, '0')}.jpg`;
}